/**
 * JP Gems & Jewels — Debtor Report (persisted, shared across devices).
 * -----------------------------------------------------------------------------
 * A simple daily ledger of who owes money: per client / invoice, the GOLD and
 * DIAMOND amount, weight and due date. The founder uses it to see which party
 * owes what and when it is due, so the team can follow up for payment.
 *
 * Mirrors the founder's Excel "Debtor Report" (PL sheet). Saved to the backend
 * database via sharedStorage under "hariom-erp-debtor", so it survives refresh
 * and server restart and is the same on every device.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { sharedStorage } from './sharedStorage'

export interface DebtorEntry {
  id: string
  date: string          // entry date (YYYY-MM-DD)
  clientName: string
  category: string      // 'B-B' | 'B-C' (business-to-business / business-to-client)
  invoiceNo: string
  // Gold
  goldWeight: number    // pure weight (grams)
  goldAmount: number    // ₹
  goldDueDate: string   // YYYY-MM-DD
  // Diamond (weight removed per founder — only amount + due date)
  diaAmount: number     // ₹ (amount / labour)
  diaDueDate: string    // YYYY-MM-DD
  salesPerson: string
  remarks: string
  flag?: boolean        // highlight a row to follow up (shown tinted + in exports)
  paid?: boolean        // payment received — removes this amount from Total Outstanding
  paidDate?: string     // when it was marked paid
}

/** Total amount owed for an entry (gold + diamond). */
export const debtorTotal = (e: DebtorEntry) => (e.goldAmount || 0) + (e.diaAmount || 0)

interface DebtorState {
  entries: DebtorEntry[]
  addEntry: (e: DebtorEntry) => void
  updateEntry: (id: string, e: DebtorEntry) => void
  setPaid: (id: string, paid: boolean) => void
  removeEntry: (id: string) => void
}

const todayISO = () => new Date().toISOString().slice(0, 10)

export const useDebtorStore = create<DebtorState>()(
  persist(
    (set) => ({
      entries: [],
      addEntry: (e) => set((s) => ({ entries: [...s.entries, e] })),
      updateEntry: (id, e) => set((s) => ({ entries: s.entries.map((x) => (x.id === id ? e : x)) })),
      setPaid: (id, paid) => set((s) => ({ entries: s.entries.map((x) => (x.id === id ? { ...x, paid, paidDate: paid ? todayISO() : '' } : x)) })),
      removeEntry: (id) => set((s) => ({ entries: s.entries.filter((x) => x.id !== id) })),
    }),
    {
      name: 'hariom-erp-debtor',
      storage: createJSONStorage(() => sharedStorage),
      partialize: (s) => ({ entries: s.entries }),
    }
  )
)
